import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TriangleAlert } from 'lucide-react';

/**
 * PAIGE: the tour's start window when the reading failed. Paige reads in the cloud, and a reading
 * can be refused (the readers down or still starting); the job behind the window then says so, but
 * the window in front went on saying "Hang tight, setting up your tour… 0 of 5 documents" with
 * nothing left to wait for (first-run walk, 2026-10-06). The job's own failure screen tells the
 * window (`TourReadingFailed`), and the window says what happened and offers the one way on: Leave
 * tour, which removes the tour's job; Take a 2-minute tour starts a fresh one.
 */
const FAILED_EVENT = 'paige-tour-reading-failed';
const failedJobs = new Set<string>();

/** Rendered by the tour job's workspace where it says its reading stopped. */
export function TourReadingFailed({ jobId }: { jobId: string }) {
  useEffect(() => {
    failedJobs.add(jobId);
    window.dispatchEvent(new Event(FAILED_EVENT));
    return () => {
      failedJobs.delete(jobId);
      window.dispatchEvent(new Event(FAILED_EVENT));
    };
  }, [jobId]);
  return null;
}

/** Whether the job's workspace is saying its reading stopped. */
export function useTourReadingFailed(jobId: string | undefined): boolean {
  const [failed, setFailed] = useState(() => Boolean(jobId && failedJobs.has(jobId)));
  useEffect(() => {
    const update = () => setFailed(Boolean(jobId && failedJobs.has(jobId)));
    update();
    window.addEventListener(FAILED_EVENT, update);
    return () => window.removeEventListener(FAILED_EVENT, update);
  }, [jobId]);
  return failed;
}

const NOT_SENT =
  'The sample documents couldn’t be sent to Cortexa just now. Try again, or leave the tour and take it later.';
const NOT_READ =
  'Cortexa couldn’t read the sample documents just now. Leave the tour and take it again in a few minutes.';

/**
 * `onRetry` is given when the samples never reached Cortexa (tour-feed.ts): then nothing was read, and
 * sending them again is the way on; a reading that failed is left and taken again later.
 */
export function TourStartFailed({
  onLeave,
  onRetry,
}: {
  onLeave: () => void;
  onRetry?: () => void;
}) {
  return createPortal(
    <div className="tour-start-backdrop">
      <section
        className="tour-start tour-start-failed"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tour-start-title"
        aria-describedby="tour-start-failed"
      >
        <header>
          <span className="tour-start-mark" aria-hidden="true">
            <TriangleAlert size={14} />
          </span>
          <h2 id="tour-start-title">The tour couldn’t start</h2>
        </header>
        <p id="tour-start-failed">{onRetry ? NOT_SENT : NOT_READ}</p>
        <footer>
          {onRetry && (
            <button type="button" className="primary-button tour-start-retry" onClick={onRetry}>
              Try again
            </button>
          )}
          <button type="button" className="tour-start-leave" onClick={onLeave}>
            Leave tour
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
